// =============================================
// SERVICE: API de Conferência de Carga
// =============================================

import { apiService } from './api';
import { buildApiUrl } from './config';
import type {
  Conferencia,
  ConferenciaItem,
  ConferenciaDocumento,
  ConferenciaLeitura,
  ConferenciaDivergencia,
  ConferenciaEstatisticas,
  ResumoLeituras,
  ResumoDivergencias,
  ConferenciaCreateData,
  ConferenciaItemCreateData,
  LeituraResponse,
  EmpresaModulo,
  TotaisLogisticos
} from '../types/conferencia';

// Helper para chamadas GET com query params
function buildUrlWithParams(path: string, params?: Record<string, unknown>): string {
  const url = buildApiUrl(path);
  if (!params) return url;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `${url}?${qs}` : url;
}

// =============================================
// CONFERÊNCIAS
// =============================================

export async function listarConferencias(params?: {
  page?: number;
  limit?: number;
  tipo?: string;
  status?: string;
  cliente_id?: string | number;
  busca?: string;
  rota?: string;
  placa_veiculo?: string;
  dataInicio?: string;
  dataFim?: string;
  numero_pedido?: string;
}) {
  return apiService.request<{
    success: boolean;
    conferencias: Conferencia[];
    pagination: { page: number; totalPages: number; total: number };
  }>(buildUrlWithParams('/conferencias', params as Record<string, unknown>));
}

export async function buscarConferencia(id: number): Promise<{
  success: boolean;
  conferencia: Conferencia;
  itens: ConferenciaItem[];
  documentos: ConferenciaDocumento[];
  divergencias: ConferenciaDivergencia[];
  resumo_leituras: ResumoLeituras;
}> {
  return apiService.request(buildApiUrl(`/conferencias/${id}`));
}

export async function criarConferencia(data: ConferenciaCreateData): Promise<{ success: boolean; conferencia: Conferencia }> {
  return apiService.request(buildApiUrl('/conferencias'), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function atualizarConferencia(id: number, data: Partial<Conferencia>): Promise<{ success: boolean; conferencia: Conferencia }> {
  return apiService.request(buildApiUrl(`/conferencias/${id}`), {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletarConferencia(id: number): Promise<{ success: boolean; message: string }> {
  return apiService.request(buildApiUrl(`/conferencias/${id}`), {
    method: 'DELETE'
  });
}

export async function obterEstatisticas(
  periodo?: string,
  filtros?: { dataInicio?: string; dataFim?: string }
): Promise<{ success: boolean; estatisticas: ConferenciaEstatisticas }> {
  return apiService.request(
    buildUrlWithParams('/conferencias/estatisticas', {
      periodo,
      dataInicio: filtros?.dataInicio,
      dataFim: filtros?.dataFim
    })
  );
}

// =============================================
// ITENS
// =============================================

export async function listarItens(conferenciaId: number): Promise<{ success: boolean; itens: ConferenciaItem[] }> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/itens`));
}

export async function adicionarItem(conferenciaId: number, data: ConferenciaItemCreateData): Promise<{ success: boolean; item: ConferenciaItem }> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/itens`), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function adicionarItensBatch(
  conferenciaId: number,
  itens: ConferenciaItemCreateData[]
): Promise<{ success: boolean; itens: ConferenciaItem[]; total: number }> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/itens/batch`), {
    method: 'POST',
    body: JSON.stringify({ itens })
  });
}

// =============================================
// DOCUMENTOS
// =============================================

export async function listarDocumentos(conferenciaId: number): Promise<{ success: boolean; documentos: ConferenciaDocumento[] }> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/documentos`));
}

export async function adicionarDocumento(conferenciaId: number, data: {
  tipo_documento: string;
  numero_documento: string;
  serie?: string;
  emitente?: string;
  destinatario?: string;
  valor_total?: number;
  peso_total?: number;
  total_volumes?: number;
}): Promise<{ success: boolean; documento: ConferenciaDocumento }> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/documentos`), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function atualizarDocumento(id: number, data: Partial<ConferenciaDocumento>): Promise<{ success: boolean; documento: ConferenciaDocumento }> {
  return apiService.request(buildApiUrl(`/conferencias/documentos/${id}`), {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletarDocumento(id: number): Promise<{ success: boolean; message: string }> {
  return apiService.request(buildApiUrl(`/conferencias/documentos/${id}`), {
    method: 'DELETE'
  });
}

// =============================================
// LEITURAS (SCANNER)
// =============================================

export async function registrarLeitura(conferenciaId: number, data: {
  tipo_leitura: string;
  codigo_lido: string;
  dados_decodificados?: Record<string, unknown>;
  quantidade?: number;
  dispositivo_tipo?: string;
  dispositivo_id?: string;
  latitude?: number;
  longitude?: number;
}): Promise<LeituraResponse> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/leituras`), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function listarLeituras(conferenciaId: number, params?: {
  limit?: number;
  offset?: number;
}): Promise<{ success: boolean; leituras: ConferenciaLeitura[] }> {
  return apiService.request(buildUrlWithParams(`/conferencias/${conferenciaId}/leituras`, params as Record<string, unknown>));
}

export async function removerLeitura(conferenciaId: number, leituraId: number, motivo?: string): Promise<{
  success: boolean;
  message: string;
  leitura_id: number;
}> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/leituras/${leituraId}/remover`), {
    method: 'PUT',
    body: JSON.stringify({ motivo })
  });
}

export async function transferirLeituraEntrePaletes(conferenciaIdDestino: number, data: {
  leitura_id_origem: number;
  conferencia_id_origem: number;
  codigo_lido: string;
  dados_decodificados?: Record<string, unknown>;
  tipo_leitura?: string;
  quantidade?: number;
}): Promise<LeituraResponse> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaIdDestino}/leituras/transferir`), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function transferirTodasLeiturasEntrePaletes(conferenciaIdDestino: number, data: {
  conferencia_id_origem: number;
}): Promise<{
  success: boolean;
  message: string;
  total_removidas: number;
  codigos_removidos: string[];
}> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaIdDestino}/leituras/transferir-todas`), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// =============================================
// DIVERGÊNCIAS
// =============================================

export async function listarDivergencias(conferenciaId: number): Promise<{
  success: boolean;
  divergencias: ConferenciaDivergencia[];
  resumo: ResumoDivergencias;
}> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/divergencias`));
}

export async function criarDivergencia(conferenciaId: number, data: {
  tipo_divergencia: string;
  item_id?: number;
  documento_id?: number;
  codigo_barras?: string;
  sku?: string;
  descricao?: string;
  quantidade_esperada?: number;
  quantidade_encontrada?: number;
  fotos?: string[];
}): Promise<{ success: boolean; divergencia: ConferenciaDivergencia }> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/divergencias`), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function resolverDivergencia(id: number, data: {
  status: 'resolvida' | 'aceita' | 'rejeitada';
  resolucao?: string;
}): Promise<{ success: boolean; divergencia: ConferenciaDivergencia }> {
  return apiService.request(buildApiUrl(`/conferencias/divergencias/${id}/resolver`), {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

// =============================================
// CARREGAMENTO - Dados do Palete
// =============================================

export async function buscarDadosPaleteCarregamento(clienteId: number | null, numeroPalete: number): Promise<{
  success: boolean;
  palete: {
    id: number;
    numero_palete: number;
    doca: string | null;
    area_entrega_id: number;
    area_nome: string;
    area_codigo: string;
    area_regiao: string;
    operacao_id: number;
    numero_operacao: number;
    tipo_palete?: string;
    data_agendamento?: string;
  };
  cliente?: {
    id: number;
    razao_social: string;
    cnpj: string;
  } | null;
  leituras: {
    codigo_lido: string;
    dados_decodificados: Record<string, unknown>;
    lido_em: string;
    tipo_leitura: string;
  }[];
  total_volumes: number;
  totais_logisticos?: TotaisLogisticos;
  pedidos?: string[];
  tem_ponto_corte?: boolean;
  pedidos_com_corte?: string[];
}> {
  const params: Record<string, any> = { numero_palete: numeroPalete };
  if (clienteId) params.cliente_id = clienteId;
  return apiService.request(buildUrlWithParams('/conferencias/carregamento/dados-palete', params));
}

// =============================================
// CARREGAMENTO - Listar Paletes (modo lista)
// =============================================

export interface PaleteCarregamento {
  id: number;
  numero_palete: number;
  doca: string | null;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  operacao_id: number;
  numero_operacao: number;
  cliente_id: number | null;
  cliente_razao_social: string | null;
  tipo_palete?: string;
  data_agendamento?: string;
  total_volumes: number;
  total_esperado: number;
  volumes_faltantes: number;
  total_duplicados?: number;
  tem_divergencia: boolean;
  disponivel: boolean;
  tem_ponto_corte?: boolean;
  pedidos_com_corte?: string[];
  peso_total?: number;
  cubagem_total?: number;
  volumes_logisticos?: number;
  pedidos?: string[];
}

export async function listarPaletesCarregamento(clienteId?: number | null): Promise<{
  success: boolean;
  paletes: PaleteCarregamento[];
  resumo: {
    total: number;
    disponiveis: number;
    divergentes: number;
    peso_total?: number;
    cubagem_total?: number;
    volumes_total?: number;
  };
}> {
  const params: Record<string, any> = {};
  if (clienteId) params.cliente_id = clienteId;
  return apiService.request(buildUrlWithParams('/conferencias/carregamento/listar-paletes', params));
}

// =============================================
// PRÉ-CARREGAMENTO - Listar Paletes (modo lista)
// =============================================

export interface PaletePreCarregamento {
  id: number;
  numero_palete: number;
  doca: string | null;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  area_entrega_id: number;
  operacao_id: number;
  numero_operacao: number;
  cliente_id: number | null;
  cliente_razao_social: string | null;
  tipo_palete?: string;
  data_agendamento?: string;
  total_leituras: number;
  cliente_cnpj?: string;
  tem_ponto_corte?: boolean;
  pedidos_com_corte?: string[];
}

export async function listarPaletesPreCarregamento(clienteId?: number | null): Promise<{
  success: boolean;
  paletes: PaletePreCarregamento[];
  resumo: { total: number };
}> {
  const params: Record<string, any> = {};
  if (clienteId) params.cliente_id = clienteId;
  return apiService.request(buildUrlWithParams('/conferencias/pre-carregamento/listar-paletes', params));
}

// =============================================
// DESCARREGAMENTO - Dados do Palete
// =============================================

export async function buscarDadosPaleteDescarregamento(numeroPalete: number): Promise<{
  success: boolean;
  palete: {
    id: number;
    numero_palete: number;
    doca: string | null;
    area_entrega_id: number;
    area_nome: string;
    area_codigo: string;
    area_regiao: string;
    operacao_id: number;
    numero_operacao: number;
    tipo_palete?: string;
    data_agendamento?: string;
  };
  cliente: {
    id: number;
    razao_social: string;
    cnpj: string;
  } | null;
  etiqueta_configs: {
    id: number;
    nome_campo: string;
    descricao?: string;
    regex_pattern: string;
    posicao_inicio?: number;
    posicao_fim?: number;
    tipo_dado: string;
    obrigatorio: boolean;
    ordem: number;
    ativo: boolean;
    modelo_id?: number;
  }[];
  etiqueta_modelos: {
    id: number;
    nome: string;
    descricao?: string;
    ativo: boolean;
    campo_numero_pedido?: string | null;
    campo_volume_atual?: string | null;
    campo_total_volumes?: string | null;
    campo_cnpj?: string | null;
    campo_codigo_setor?: string | null;
    total_campos: number;
    configs: {
      id: number;
      nome_campo: string;
      regex_pattern: string;
      tipo_dado: string;
      obrigatorio: boolean;
      ordem: number;
      ativo: boolean;
    }[];
  }[];
  leituras: {
    codigo_lido: string;
    dados_decodificados: Record<string, unknown>;
    lido_em: string;
    tipo_leitura: string;
  }[];
  total_volumes: number;
  totais_logisticos?: TotaisLogisticos;
  tem_ponto_corte?: boolean;
  pedidos_com_corte?: string[];
}> {
  return apiService.request(buildUrlWithParams('/conferencias/descarregamento/dados-palete', {
    numero_palete: numeroPalete
  }));
}

// =============================================
// DESCARREGAMENTO - Identificar Palete por Etiqueta
// =============================================

export async function identificarPaletePorEtiqueta(codigoEtiqueta: string): Promise<{
  success: boolean;
  encontrado?: boolean;
  message?: string;
  palete?: {
    id: number;
    numero_palete: number;
    doca: string | null;
    area_entrega_id: number;
    area_nome: string;
    area_codigo: string;
    area_regiao: string;
    operacao_id: number;
    numero_operacao: number;
    tipo_palete?: string;
    data_agendamento?: string;
  };
  cliente?: {
    id: number;
    razao_social: string;
    cnpj: string;
  } | null;
  etiqueta_configs?: {
    id: number;
    nome_campo: string;
    descricao?: string;
    regex_pattern: string;
    posicao_inicio?: number;
    posicao_fim?: number;
    tipo_dado: string;
    obrigatorio: boolean;
    ordem: number;
    ativo: boolean;
    modelo_id?: number;
  }[];
  etiqueta_modelos?: {
    id: number;
    nome: string;
    descricao?: string;
    ativo: boolean;
    campo_numero_pedido?: string | null;
    campo_volume_atual?: string | null;
    campo_total_volumes?: string | null;
    campo_cnpj?: string | null;
    campo_codigo_setor?: string | null;
    total_campos: number;
    configs: {
      id: number;
      nome_campo: string;
      regex_pattern: string;
      tipo_dado: string;
      obrigatorio: boolean;
      ordem: number;
      ativo: boolean;
    }[];
  }[];
  leituras?: {
    id: number;
    conferencia_id: number;
    codigo_lido: string;
    dados_decodificados: Record<string, unknown>;
    lido_em: string;
    tipo_leitura: string;
    resultado: string;
  }[];
  total_volumes?: number;
  totais_logisticos?: TotaisLogisticos;
  tem_ponto_corte?: boolean;
  pedidos_com_corte?: string[];
}> {
  return apiService.request(buildUrlWithParams('/conferencias/descarregamento/identificar-palete', {
    codigo_etiqueta: codigoEtiqueta
  }));
}

// =============================================
// ANÁLISE DE VOLUMES
// =============================================

export interface AnaliseVolumePedido {
  numero_pedido: string;
  total_volumes: number;
  volumes_conferidos: number[];
  volumes_faltantes: number[];
  quantidade_conferida: number;
  quantidade_faltante: number;
  status: 'completo' | 'incompleto' | 'pendente';
  tem_ponto_corte?: boolean;
  leituras: {
    codigo_lido: string;
    volume_atual: number;
    total_volumes: number;
    lido_em: string;
    resultado: 'ok' | 'duplicado' | 'nao_encontrado' | 'removido';
  }[];
}

export interface ResumoAnaliseVolumes {
  total_pedidos: number;
  pedidos_completos: number;
  pedidos_incompletos: number;
  total_volumes_esperados: number;
  total_volumes_conferidos: number;
  total_volumes_faltantes: number;
}

export async function analisarDivergenciasVolumes(conferenciaId: number): Promise<{
  success: boolean;
  analise_volumes: AnaliseVolumePedido[];
  resumo: ResumoAnaliseVolumes;
  message?: string;
}> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/analise-volumes`));
}

// =============================================
// REMOÇÃO DE LEITURAS DE PEDIDOS DIVERGENTES
// =============================================

export async function removerLeiturasPedidosDivergentes(conferenciaId: number, data: {
  numero_pedidos: string[];
  motivo?: string;
}): Promise<{
  success: boolean;
  message: string;
  total_removidas: number;
  pedidos_processados: string[];
}> {
  return apiService.request(buildApiUrl(`/conferencias/${conferenciaId}/leituras/remover-pedidos`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// =============================================
// TOTAIS LOGÍSTICOS
// =============================================

export async function buscarTotaisLogisticos(conferenciaId: number, numeroPalete?: number): Promise<{
  success: boolean;
  totais_logisticos: TotaisLogisticos;
  total_leituras: number;
}> {
  const params: Record<string, unknown> = {};
  if (numeroPalete) params.numero_palete = numeroPalete;
  return apiService.request(buildUrlWithParams(`/conferencias/${conferenciaId}/totais-logisticos`, params));
}

// =============================================
// MÓDULOS DA EMPRESA
// =============================================

export async function listarModulosEmpresa(): Promise<{ success: boolean; modulos: EmpresaModulo[] }> {
  return apiService.request(buildApiUrl('/empresa/modulos'));
}

export async function ativarModulo(modulo: string, empresaId?: number): Promise<{ success: boolean; modulo: EmpresaModulo }> {
  return apiService.request(buildApiUrl('/empresa/modulos'), {
    method: 'POST',
    body: JSON.stringify({ modulo, empresa_id: empresaId })
  });
}

export async function desativarModulo(modulo: string): Promise<{ success: boolean; message: string }> {
  return apiService.request(buildApiUrl(`/empresa/modulos/${modulo}`), {
    method: 'DELETE'
  });
}
